import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

interface OnboardingState {
    shouldShow: boolean;
    isLoading: boolean;
    markAsCompleted: () => Promise<void>;
}

export function useOnboarding(): OnboardingState {
    const { user } = useAuth();
    const [shouldShow, setShouldShow] = useState(false);
    const [isLoading, setIsLoading] = useState(true);

    useEffect(() => {
        if (!user) {
            setShouldShow(false);
            setIsLoading(false);
            return;
        }

        const checkOnboardingStatus = async () => {
            try {
                // First check localStorage for quick response
                const localData = localStorage.getItem('publee_onboarding');
                if (localData) {
                    const parsed = JSON.parse(localData);
                    if (parsed.completed_at && parsed.user_id === user.id) {
                        setShouldShow(false);
                        setIsLoading(false);
                        return;
                    }
                }

                // Check database
                const { data: profile, error } = await supabase
                    .from('profiles')
                    .select('onboarding_completed_at')
                    .eq('id', user.id)
                    .single() as { data: { onboarding_completed_at: string | null } | null, error: any };

                if (error) {
                    console.error('Error checking onboarding:', error);
                    // If error (column doesn't exist), check localStorage only
                    setShouldShow(!localData);
                } else {
                    // Show onboarding if not completed
                    setShouldShow(!profile?.onboarding_completed_at);
                }
            } catch (err) {
                console.error('Onboarding check error:', err);
                setShouldShow(false);
            } finally {
                setIsLoading(false);
            }
        };

        checkOnboardingStatus();
    }, [user]);

    const markAsCompleted = useCallback(async () => {
        if (!user) return;

        // Update localStorage immediately
        const existingData = localStorage.getItem('publee_onboarding');
        const data = existingData ? JSON.parse(existingData) : {};
        localStorage.setItem('publee_onboarding', JSON.stringify({
            ...data,
            completed_at: new Date().toISOString(),
            user_id: user.id,
        }));

        setShouldShow(false);

        // Try to update database
        try {
            await supabase
                .from('profiles')
                .update({ onboarding_completed_at: new Date().toISOString() } as any)
                .eq('id', user.id);
        } catch (err) {
            console.error('Error marking onboarding complete:', err);
        }
    }, [user]);

    return {
        shouldShow,
        isLoading,
        markAsCompleted,
    };
}
